import React from 'react';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import PremiumDropdown from '../../PremiumDropdown';
import { SubHeaderVisibilityDropdown } from '../dropdowns';

interface SubHeaderCalendarNavControlsProps {
  calendarDate: Date;
  setCalendarDate: (date: Date) => void;
  calendarViewType: 'month' | 'week';
  setCalendarViewType: (type: 'month' | 'week') => void;
  calendarFilterMode?: 'all' | 'tasks' | 'milestones' | 'deadlines';
  setCalendarFilterMode?: (mode: 'all' | 'tasks' | 'milestones' | 'deadlines') => void;
  showCompleted?: boolean;
  setShowCompleted?: (val: boolean) => void;
  showArchived?: boolean;
  setShowArchived?: (val: boolean) => void;
  showTaskDetails?: boolean;
  setShowTaskDetails?: (val: boolean) => void;
}

export const SubHeaderCalendarNavControls: React.FC<SubHeaderCalendarNavControlsProps> = ({
  calendarDate,
  setCalendarDate,
  calendarViewType,
  setCalendarViewType,
  calendarFilterMode = 'all',
  setCalendarFilterMode,
  showCompleted,
  setShowCompleted,
  showArchived,
  setShowArchived,
  showTaskDetails,
  setShowTaskDetails
}) => {
  return (
    <div className="flex items-center gap-2 mr-2">
      <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl border border-slate-200/50 dark:border-slate-800 h-10 items-center shadow-inner gap-2">
        {/* 1. Month/Week Switcher */}
        <div className="flex bg-white dark:bg-slate-900/60 p-0.5 rounded-lg border border-slate-200/30 dark:border-slate-700/30">
          {(['month', 'week'] as const).map((type) => (
            <button 
              key={type}
              type="button"
              onClick={() => setCalendarViewType(type)} 
              className={`px-3 py-1 rounded-md text-[9px] font-bold uppercase tracking-widest transition-all ${
                calendarViewType === type 
                  ? 'bg-amber-600 text-white shadow-sm font-black' 
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {type === 'month' ? 'Monat' : 'Woche'}
            </button>
          ))}
        </div>

        <div className="h-4 w-px bg-slate-200 dark:bg-slate-700/60 mx-0.5" />

        {/* 2. Navigation */}
        <div className="flex items-center">
          <button 
            type="button"
            onClick={() => {
              const d = new Date(calendarDate);
              if (calendarViewType === 'month') d.setMonth(d.getMonth() - 1);
              else d.setDate(d.getDate() - 7);
              setCalendarDate(d);
            }} 
            className="p-1 px-1.5 hover:bg-white dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-amber-600 transition-all active:scale-95"
            title="Vorherige Periode"
          >
            <ChevronLeft size={14} strokeWidth={3} />
          </button>
          <button 
            type="button"
            onClick={() => setCalendarDate(new Date())}
            className="px-2 h-7 rounded-lg text-[9px] font-bold uppercase tracking-widest text-slate-500 hover:text-amber-600 hover:bg-white dark:hover:bg-slate-700 transition-all active:scale-95"
          >
            Heute
          </button>
          <button 
            type="button"
            onClick={() => {
              const d = new Date(calendarDate);
              if (calendarViewType === 'month') d.setMonth(d.getMonth() + 1);
              else d.setDate(d.getDate() + 7);
              setCalendarDate(d);
            }} 
            className="p-1 px-1.5 hover:bg-white dark:hover:bg-slate-700 rounded-lg text-slate-400 hover:text-amber-600 transition-all active:scale-95"
            title="Nächste Periode"
          >
            <ChevronRight size={14} strokeWidth={3} />
          </button>
        </div>
      </div>

      {/* Filter Dropdown */}
      {setCalendarFilterMode && (
        <PremiumDropdown
          value={calendarFilterMode}
          onChange={(val) => setCalendarFilterMode(val as any)}
          options={[
            { value: 'all', label: 'Alle' },
            { value: 'tasks', label: 'Aufgaben' },
            { value: 'milestones', label: 'Meilensteine' },
            { value: 'deadlines', label: 'Fristen' }
          ]}
          icon={<Filter size={13} />}
          placeholder="Filter"
          showLabel={true}
          variant="ghost"
          className={`h-[34px] border rounded-xl transition-all shadow-sm flex items-center justify-center min-w-[130px] ${
            calendarFilterMode !== 'all'
              ? 'bg-indigo-50 border-indigo-200 text-indigo-600 dark:bg-indigo-950/30 dark:border-indigo-800 dark:text-indigo-400 font-bold shadow-sm'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200/60 dark:border-slate-800 text-slate-500 hover:text-indigo-600 hover:bg-white dark:hover:bg-slate-750'
          }`}
        />
      )}

      {/* Visibility Dropdown (Anzeige) */}
      <SubHeaderVisibilityDropdown
        showCompleted={showCompleted}
        setShowCompleted={setShowCompleted}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        showTaskDetails={showTaskDetails}
        setShowTaskDetails={setShowTaskDetails}
      />
    </div>
  );
};
